// Which vendor product an order is placed against — ONE implementation, two
// runtimes.
//
// This rule used to live only in the browser (`utils/itemCostSummary.js`), so
// the server's count→PO bucket builder had no way to ask it. It answered the
// question the only way it could: read the row's explicit
// `count_items.selected_purchase_option_id`, and if that was NULL — which it
// is for every row until somebody chooses — order from nothing at all. A
// count of four Southern Glazer's liquors, each with exactly one active,
// priced purchase option, produced four unlinked $0.00 lines at fractional
// bottle quantities, because the modern purchase logic never ran.
//
// NULL does not mean "ignore all purchase options". It means "resolve the
// purchasing choice at read time" (docs/multi-vendor-purchasing.md, "Choosing
// the vendor for a count line"). Moving the resolver here is what lets both
// the Ordering Hub preview and `purchase-order-dispatch` say so.
//
// Pure ESM. No Deno APIs, no I/O, no browser APIs.

import { assembleStoredComparison, selectAutomaticOption } from "./priceComparison.mjs";
import { vendorIsRetired } from "./vendorState.mjs";
import { hasPackageBasisConflict } from "./packageBasis.mjs";
import { activeOptions, preferredOption, toPositiveNumber } from "./canonicalItemCost.mjs";

/**
 * Options eligible to be offered for buying: active, approved, not hidden
 * from allocation, and actually priced. Mirrors
 * utils/purchaseOptions#listAllocatableOptions' filter — an unapproved or
 * hidden product must never be called "best price", and a "no price provided"
 * option is a visible nudge, never a purchasable offer (or a $0 price).
 */
export function offerableOptions(options) {
  return activeOptions(options).filter(
    (option) =>
      !!option.is_approved &&
      !option.is_hidden_from_allocation &&
      // A retired vendor's product may still describe stock on the shelf,
      // but it is never an OFFER — its price and MOQ stay out of every
      // new-order calculation.
      !vendorIsRetired(option) &&
      // A package whose stored facts conflict (qty_per_package vs inner-pack
      // structure vs the deterministic comparison) is not an offer either —
      // there is no agreed package for its price to describe.
      !hasPackageBasisConflict(option) &&
      (toPositiveNumber(option.package_price) !== null ||
        toPositiveNumber(option.normalized_unit_cost) !== null),
  );
}

/**
 * Which vendor product a count line will be bought from, and why.
 *
 * The default IS automatic: with no explicit choice, the line buys from the
 * lowest trustworthy comparable price under the item's relationship
 * comparison (priceComparison.mjs) — the comparison unit makes "cheapest" a
 * real claim instead of a guess across unlike packages. The fallbacks exist
 * for when that claim cannot be made: an unresolved comparison (unsized
 * packages, an AI estimate below the confidence threshold, no priced eligible
 * option) falls back to the preferred vendor, then to the only active option,
 * and finally reports 'unresolved'.
 *
 * Order: explicit → automatic lowest comparable → preferred → only → nothing.
 * An explicit id that no longer resolves to an active option falls into the
 * same automatic→preferred chain with `isStale` true, so the UI says the
 * pinned product is gone instead of silently swapping vendors.
 *
 * @param {Array}  options            purchase_options rows (any status)
 * @param {*}      [selectedOptionId] count_items.selected_purchase_option_id
 * @param {object} [context]          { item, now } — `item` carries the stored
 *                                    relationship comparison (comparison_*
 *                                    columns). Omitted, nothing resolves and
 *                                    the preferred/only chain answers, as
 *                                    before the comparison existed.
 * @returns {{option: Object|null,
 *            reason: 'explicit'|'best_price'|'best_price_ai_estimate'|
 *                    'tied_best_price'|'preferred_fallback'|'only'|'unresolved',
 *            isExplicit: boolean, isStale: boolean,
 *            usedStaleComparison: boolean, comparison: Object}}
 *          `usedStaleComparison` is true when the automatic winner's price is
 *          outside the freshness window (every candidate was stale).
 *          `comparison` is the assembled stored comparison, so callers can
 *          render comparable prices without re-deriving it.
 */
export function resolveSelectedOption(options, selectedOptionId, { item = null, now = Date.now() } = {}) {
  const active = activeOptions(options);
  const comparison = assembleStoredComparison(item || {}, options || []);

  const settle = (option, reason, isStale, usedStaleComparison = false) => ({
    option, reason, isExplicit: false, isStale, usedStaleComparison, comparison,
  });

  const automaticChain = (isStale) => {
    const auto = selectAutomaticOption({ options: options || [], comparison, now });
    if (auto.optionId != null) {
      const winner = active.find((option) => String(option.id) === String(auto.optionId));
      if (winner) return settle(winner, auto.reason, isStale, auto.usedStale);
    }
    const preferred = preferredOption(active);
    if (preferred) return settle(preferred, 'preferred_fallback', isStale);
    if (active.length === 1) return settle(active[0], 'only', isStale);
    return settle(null, 'unresolved', isStale);
  };

  if (selectedOptionId != null && selectedOptionId !== '') {
    const chosen = active.find((option) => String(option.id) === String(selectedOptionId));
    // A retired vendor's product is treated exactly like a product that is
    // gone: the picker says so instead of buying from a vendor the store has
    // stopped ordering from.
    if (chosen && !vendorIsRetired(chosen)) {
      return {
        option: chosen, reason: 'explicit', isExplicit: true, isStale: false,
        usedStaleComparison: false, comparison,
      };
    }
    // The pinned product is gone. Fall into the automatic chain, but say so.
    return automaticChain(true);
  }

  return automaticChain(false);
}

/**
 * The same rule, narrowed to products that could actually BE ordered.
 *
 * `resolveSelectedOption`'s preferred/only fallbacks deliberately run over
 * every ACTIVE option, because the vendor picker has to be able to show a
 * pinned product that has no price yet. An order may not: an unpriced,
 * unapproved, hidden, or package-conflicted product cannot produce a legal
 * PO line, and offering it here would only push the line back onto the
 * legacy fallback one step later. So direct ordering asks the one resolver
 * over the OFFERABLE set — no second selection algorithm, just a narrower
 * question.
 *
 * The count row's explicit choice still leads; it simply has to be a product
 * this store can buy today.
 *
 * @returns the `resolveSelectedOption` shape, plus `eligibleCount` — how many
 *          offerable products the item (or its pooled group) had, which is
 *          what tells "exactly one option, use it" apart from "several, the
 *          comparison chose".
 */
export function resolveDirectOrderOption(options, selectedOptionId, context = {}) {
  const eligible = offerableOptions(options);
  const resolution = resolveSelectedOption(eligible, selectedOptionId, context);
  return { ...resolution, eligibleCount: eligible.length, eligibleOptions: eligible };
}
